// Generat automat din README.md - Advanced Export Modal Component
import React, { useState, useEffect } from 'react';
import { Download, FileText, Table, FileSpreadsheet, X, Check, Filter, ArrowUp, ArrowDown, Settings } from 'lucide-react';
import Modal from './Modal';
import Badge from './Badge';

const ExportModal = ({ 
  isOpen, 
  onClose, 
  data, 
  moduleType = 'general',
  onExport,
  title = 'Export Date'
}) => {
  const [exportConfig, setExportConfig] = useState({
    format: 'csv',
    filename: '',
    title: title,
    selectedColumns: [],
    filters: {},
    sorting: { key: '', direction: 'asc' }
  });

  const [availableColumns, setAvailableColumns] = useState([]);
  const [previewData, setPreviewData] = useState([]);
  const [activeTab, setActiveTab] = useState('format');
  const [isExporting, setIsExporting] = useState(false);

  // Initialize columns when modal opens
  useEffect(() => {
    if (isOpen && data && data.length > 0) {
      const columns = Object.keys(data[0]).map(key => ({
        key,
        displayName: formatColumnName(key),
        selected: true
      }));
      setAvailableColumns(columns);
      setExportConfig(prev => ({
        ...prev,
        selectedColumns: columns.filter(col => col.selected).map(col => col.key),
        filename: `export-${moduleType}-${new Date().toISOString().split('T')[0]}`
      }));
      setPreviewData(data.slice(0, 5)); // Show first 5 rows for preview
    }
  }, [isOpen, data, moduleType]);

  const formatColumnName = (key) => {
    const mappings = {
      id: 'ID',
      name: 'Nume',
      email: 'Email',
      phone: 'Telefon',
      status: 'Status',
      createdAt: 'Data Creare',
      updatedAt: 'Data Actualizare',
      totalValue: 'Valoare Totală',
      contactPerson: 'Persoană Contact'
    };
    return mappings[key] || key.charAt(0).toUpperCase() + key.slice(1);
  };

  const handleColumnToggle = (columnKey) => {
    const updatedColumns = availableColumns.map(col => 
      col.key === columnKey ? { ...col, selected: !col.selected } : col
    );
    setAvailableColumns(updatedColumns);
    setExportConfig(prev => ({
      ...prev,
      selectedColumns: updatedColumns.filter(col => col.selected).map(col => col.key)
    }));
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await onExport(exportConfig);
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      setIsExporting(false);
      onClose();
    }
  };

  const formatOptions = [
    { key: 'csv', name: 'CSV', icon: FileText, description: 'Format text cu virgulă' },
    { key: 'excel', name: 'Excel', icon: FileSpreadsheet, description: 'Fișier Excel (.xlsx)' },
    { key: 'pdf', name: 'PDF', icon: Download, description: 'Document PDF' }
  ];

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Export Date Avansat">
      <div className="max-w-4xl mx-auto">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
          <nav className="-mb-px flex space-x-8">
            {[
              { key: 'format', name: 'Format & Fișier', icon: Settings },
              { key: 'columns', name: 'Selectare Coloane', icon: Table },
              { key: 'preview', name: 'Previzualizare', icon: FileText }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.key
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span>{tab.name}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {/* Format & File Tab */}
          {activeTab === 'format' && (
            <div className="space-y-6">
              {/* Format Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Format Export
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {formatOptions.map(format => (
                    <div
                      key={format.key}
                      onClick={() => setExportConfig(prev => ({ ...prev, format: format.key }))}
                      className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                        exportConfig.format === format.key
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <div className="flex items-center space-x-3 mb-2">
                        <format.icon className="w-5 h-5 text-primary-600" />
                        <span className="font-medium text-gray-900 dark:text-white">
                          {format.name}
                        </span>
                        {exportConfig.format === format.key && (
                          <Check className="w-4 h-4 text-primary-600 ml-auto" />
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {format.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* File Configuration */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Nume Fișier
                  </label>
                  <input
                    type="text"
                    value={exportConfig.filename}
                    onChange={(e) => setExportConfig(prev => ({ ...prev, filename: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                    placeholder="export-data"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Titlu Document
                  </label>
                  <input
                    type="text"
                    value={exportConfig.title}
                    onChange={(e) => setExportConfig(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Export Date"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Columns Tab */}
          {activeTab === 'columns' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Selectează Coloanele pentru Export
                </h3>
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      const allSelected = availableColumns.map(col => ({ ...col, selected: true }));
                      setAvailableColumns(allSelected);
                      setExportConfig(prev => ({ ...prev, selectedColumns: allSelected.map(col => col.key) }));
                    }}
                    className="px-3 py-1 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700"
                  >
                    Selectează Tot
                  </button>
                  <button
                    onClick={() => {
                      const noneSelected = availableColumns.map(col => ({ ...col, selected: false }));
                      setAvailableColumns(noneSelected);
                      setExportConfig(prev => ({ ...prev, selectedColumns: [] }));
                    }}
                    className="px-3 py-1 text-sm border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    Deselectează Tot
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {availableColumns.map(column => (
                  <div
                    key={column.key}
                    className="flex items-center space-x-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg"
                  >
                    <input
                      type="checkbox"
                      id={column.key}
                      checked={column.selected}
                      onChange={() => handleColumnToggle(column.key)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <label
                      htmlFor={column.key}
                      className="flex-1 text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer"
                    >
                      {column.displayName}
                    </label>
                  </div>
                ))}
              </div>

              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  <strong>{exportConfig.selectedColumns.length}</strong> din <strong>{availableColumns.length}</strong> coloane selectate
                </p>
              </div>
            </div>
          )}

          {/* Preview Tab */}
          {activeTab === 'preview' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Previzualizare Export
                </h3>
                <Badge variant="info">
                  {data?.length || 0} înregistrări totale
                </Badge>
              </div>

              {previewData.length > 0 ? (
                <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        {exportConfig.selectedColumns.map(columnKey => (
                          <th
                            key={columnKey}
                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                          >
                            {formatColumnName(columnKey)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                      {previewData.map((row, index) => (
                        <tr key={index}>
                          {exportConfig.selectedColumns.map(columnKey => (
                            <td
                              key={columnKey}
                              className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-300"
                            >
                              {row[columnKey] || '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  Nu există date pentru previzualizare
                </div>
              )}

              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  Aceasta este o previzualizare cu primele 5 înregistrări. Fișierul final va conține toate {data?.length || 0} înregistrările.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between pt-6 border-t border-gray-200 dark:border-gray-700 mt-8">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Format: <strong className="text-gray-900 dark:text-white">{exportConfig.format.toUpperCase()}</strong> • 
            Coloane: <strong className="text-gray-900 dark:text-white">{exportConfig.selectedColumns.length}</strong> • 
            Înregistrări: <strong className="text-gray-900 dark:text-white">{data?.length || 0}</strong>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
            >
              Anulează
            </button>
            <button
              onClick={handleExport}
              disabled={isExporting || exportConfig.selectedColumns.length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isExporting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Se exportă...</span>
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  <span>Exportă {exportConfig.format.toUpperCase()}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ExportModal;